/**
 * Come si SCRIVE un istante della sezione Limulo.
 *
 * Sta in un modulo puro — l'orologio arriva come parametro, la lingua come parametro —
 * perche' il cancello (`tools/verify-limulo.ts`, Node) possa esercitarlo: una formattazione
 * dentro un `.tsx` e' fuori dalla sua portata, e questa sezione ha gia' pagato una volta il
 * prezzo di una funzione corretta che nessuno chiamava.
 *
 * ⚠️ **L'ingresso e' un `number | null` che viene da `normalizeInstant`, non una stringa dal
 * filo.** Il provider nativo manda numeri e `Date.parse` su un numero da' `NaN`: chi passa
 * qui un campo grezzo salta l'unico parser di date della sezione. Il tipo lo rende difficile
 * apposta.
 *
 * ⚠️ `null` non diventa un trattino: torna `null`, e **chi rende sceglie la frase**. Un «—»
 * deciso qui direbbe la stessa cosa in tre schermate che hanno tre domande diverse — la
 * linea del tempo scrive «Data non dichiarata», il riquadro di testata scrive altro.
 */
import { format, formatDistanceStrict, isSameDay } from 'date-fns';

import { localeDateFns } from '../fmt.ts';
import { chiusuraCheckInDicibile } from './model.ts';

// La mappa lingua → locale di `date-fns` e' una sola, in `lib/fmt.ts` (it, en, es, el): la lingua di i18next
// puo' essere `it-IT` e li' conta la parte base. `fmt.ts` e' importabile da qui (nessun `@/`, nessun catalogo).

/** «gio 18 set, 09:00» — l'istante per esteso, quando serve sapere QUANDO. */
export function instantLong(at: number | null, lang: string): string | null {
  if (at === null) return null;
  return format(at, 'EEE d MMM, HH:mm', { locale: localeDateFns(lang) });
}

/** «18 set, 09:00» — la forma corta dei riquadri di testata, dove lo spazio e' poco. */
export function instantShort(at: number | null, lang: string): string | null {
  if (at === null) return null;
  return format(at, 'd MMM, HH:mm', { locale: localeDateFns(lang) });
}

/**
 * «ven 18 set, 17:45–18:00» — una FINESTRA, coi due capi nello stesso giorno; in giorni diversi
 * «ven 18 set, 23:45 – sab 19 set, 00:15». `null` se manca un capo: chi rende sceglie la frase con un
 * capo solo, e non si inventa l'altro.
 * ⚠️ «Stesso giorno» e' quello del telefono (il fuso con cui si scrivono le ore), non quello UTC.
 */
export function windowLong(from: number | null, to: number | null, lang: string): string | null {
  if (from === null || to === null) return null;
  const locale = localeDateFns(lang);
  if (isSameDay(from, to)) {
    return `${format(from, 'EEE d MMM, HH:mm', { locale })}–${format(to, 'HH:mm', { locale })}`;
  }
  return `${format(from, 'EEE d MMM, HH:mm', { locale })} – ${format(to, 'EEE d MMM, HH:mm', { locale })}`;
}

/**
 * «fra 2 giorni», «3 ore fa» — la distanza dall'istante dato.
 *
 * ⚠️ `now` e' un PARAMETRO e non `Date.now()`: la scheda legge l'orologio una volta per
 * render e lo passa in giu'. Con ogni riga che lo legge per conto suo, due voci della stessa
 * linea del tempo possono raccontare due presenti diversi — ed e' lo stesso motivo per cui
 * la lista dei tornei fa gia' cosi'.
 */
export function instantDistance(at: number | null, now: number, lang: string): string | null {
  if (at === null) return null;
  return formatDistanceStrict(at, now, { addSuffix: true, locale: localeDateFns(lang) });
}

/**
 * La riga della FINESTRA del check-in al torneo (kb#303 §2): quale frase, e con cosa dentro. Pura, perche'
 * la scelta fra i tre casi si provi senza montare la barra.
 *  · i due capi ⇒ «Finestra prevista del check-in: ven 18 set, 17:45–18:00.»;
 *  · solo l'apertura, o solo la chiusura ⇒ la frase di quel capo, per esteso;
 *  · 🔴 durata ZERO (chiusura == apertura: revisione 1 di #485, difetto 2) ⇒ solo l'apertura. Il calendario
 *    del motore la ammette, e il motore non la rispetta: vale il default di 15' dall'apertura vera.
 *  · 🔴 ROVESCIATA (chiusura prima dell'apertura: revisione 2, difetto nuovo 2) ⇒ nessuna riga. Nasce solo da
 *    documenti scritti prima della validazione del calendario, e li' il motore apre all'AVVIO, non
 *    all'apertura scritta: dire quell'apertura manderebbe il giocatore dopo la chiusura, cioe' al ritiro.
 * «Prevista» perche' e' la finestra come la vedra' lo scheduler: se apre in ritardo, resta aperta almeno 15'
 * dall'apertura vera (kb#303 §2), e il bottone segue il server.
 *  · nessuno dei due ⇒ `null`: niente riga (il BFF servito prima di `qlash-limulo#169`).
 * ⛔ Nessun ramo guarda l'orologio: la finestra si dice uguale prima, durante e dopo. Scegliere fra «apre
 * alle» e «si e' chiusa alle» leggendo l'ora del telefono sarebbe una deduzione che il server non ha fatto.
 */
export function checkInWindowPhrase(
  finestra: { opensAt: number | null; closesAt: number | null },
  lang: string,
): { chiave: string; valori: Record<string, string> } | null {
  if (finestra.opensAt !== null && finestra.closesAt !== null && finestra.closesAt < finestra.opensAt) return null;
  const entrambi = chiusuraCheckInDicibile(finestra.opensAt, finestra.closesAt)
    ? windowLong(finestra.opensAt, finestra.closesAt, lang)
    : null;
  if (entrambi !== null) return { chiave: 'register.checkin.window', valori: { finestra: entrambi } };
  const apre = instantLong(finestra.opensAt, lang);
  if (apre !== null) return { chiave: 'register.checkin.opensAt', valori: { when: apre } };
  const chiude = instantLong(finestra.closesAt, lang);
  if (chiude !== null) return { chiave: 'register.checkin.closesAt', valori: { when: chiude } };
  return null;
}
